import React, { useState } from 'react';
import { Card, List, Button, Modal, Form, Input, message, Popconfirm, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { useApiPresets } from '../hooks/useApiPresets';
import { useApiConfig } from '../hooks/useApiConfig';

const ApiPresets = () => {
  const {
    presets,
    activePresetId,
    activePreset,
    isLoading,
    addPreset,
    updatePreset,
    deletePreset,
    activatePreset,
    resetToDefaults,
  } = useApiPresets();

  const { saveApiUrl, testConnection } = useApiConfig();

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [testingPresetId, setTestingPresetId] = useState(null);

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Add Preset Handler
  const handleAddPreset = async (values) => {
    const result = await addPreset(values);
    if (result.success) {
      message.success('Preset erfolgreich hinzugefügt');
      setIsAddModalVisible(false);
      addForm.resetFields();
    } else {
      message.error(result.error || 'Fehler beim Hinzufügen');
    }
  };

  // Edit Preset Handler
  const handleEditPreset = async (values) => {
    const result = await updatePreset(editingPreset.id, values);
    if (result.success) {
      message.success('Preset erfolgreich aktualisiert');
      setIsEditModalVisible(false);
      setEditingPreset(null);
      editForm.resetFields();
    } else {
      message.error(result.error || 'Fehler beim Aktualisieren');
    }
  };

  // Delete Preset Handler
  const handleDeletePreset = async (presetId) => {
    const result = await deletePreset(presetId);
    if (result.success) {
      message.success('Preset gelöscht');
    } else {
      message.error(result.error || 'Fehler beim Löschen');
    }
  };

  // Activate Preset Handler
  const handleActivatePreset = async (preset) => {
    const result = await activatePreset(preset.id);
    if (result.success) {
      // Update API URL
      await saveApiUrl(preset.url);
      message.success(`Wechsel zu: ${preset.name}`);
    } else {
      message.error(result.error || 'Fehler beim Aktivieren');
    }
  };

  // Test Connection Handler
  const handleTestConnection = async (preset) => {
    setTestingPresetId(preset.id);
    const result = await testConnection(preset.url);
    setTestingPresetId(null);

    if (result.success) {
      message.success(`Verbindung erfolgreich (HTTP ${result.status})`);
    } else {
      message.error(result.message || 'Verbindung fehlgeschlagen');
    }
  };

  // Open Edit Modal
  const openEditModal = (preset) => {
    setEditingPreset(preset);
    editForm.setFieldsValue({
      name: preset.name,
      url: preset.url,
      description: preset.description,
    });
    setIsEditModalVisible(true);
  };

  if (isLoading) {
    return <Card loading />;
  }

  return (
    <Card
      title={
        <>
          <GlobalOutlined /> Server Presets
        </>
      }
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalVisible(true)}
          >
            Neu
          </Button>
          <Popconfirm
            title="Alle Presets zurücksetzen?"
            description="Dies setzt alle Presets auf Standard zurück."
            onConfirm={resetToDefaults}
            okText="Ja"
            cancelText="Nein"
          >
            <Button>Zurücksetzen</Button>
          </Popconfirm>
        </Space>
      }
    >
      <List
        dataSource={presets}
        renderItem={(preset) => {
          const isActive = preset.id === activePresetId;
          const isTesting = preset.id === testingPresetId;

          return (
            <List.Item
              key={preset.id}
              style={{
                backgroundColor: isActive ? '#f0f5ff' : 'transparent',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '8px',
              }}
              actions={[
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  type={isActive ? 'primary' : 'default'}
                  onClick={() => handleActivatePreset(preset)}
                  disabled={isActive}
                >
                  {isActive ? 'Aktiv' : 'Aktivieren'}
                </Button>,
                <Button
                  size="small"
                  loading={isTesting}
                  onClick={() => handleTestConnection(preset)}
                >
                  Test
                </Button>,
                !preset.isDefault && (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(preset)}
                  />
                ),
                !preset.isDefault && (
                  <Popconfirm
                    title="Preset löschen?"
                    onConfirm={() => handleDeletePreset(preset.id)}
                    okText="Ja"
                    cancelText="Nein"
                  >
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={isActive}
                    />
                  </Popconfirm>
                ),
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <Space>
                    {preset.name}
                    {preset.isDefault && <Tag color="blue">Standard</Tag>}
                    {isActive && <Tag color="green">Aktiv</Tag>}
                  </Space>
                }
                description={
                  <div>
                    <div style={{ color: '#666', marginBottom: 4 }}>{preset.url}</div>
                    {preset.description && (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {preset.description}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />

      {/* Add Preset Modal */}
      <Modal
        title="Neues Server Preset"
        open={isAddModalVisible}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        onOk={() => addForm.submit()}
        okText="Hinzufügen"
        cancelText="Abbrechen"
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddPreset}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Bitte Name eingeben' }]}
          >
            <Input placeholder="z.B. Jochens Server" />
          </Form.Item>
          <Form.Item
            name="url"
            label="URL"
            rules={[
              { required: true, message: 'Bitte URL eingeben' },
              {
                pattern: /^https?:\/\/.+/,
                message: 'Muss mit http:// oder https:// beginnen',
              },
            ]}
          >
            <Input placeholder="https://api.example.com" />
          </Form.Item>
          <Form.Item name="description" label="Beschreibung (optional)">
            <Input.TextArea rows={2} placeholder="Optionale Beschreibung" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Preset Modal */}
      <Modal
        title="Preset bearbeiten"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingPreset(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditPreset}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Bitte Name eingeben' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="url"
            label="URL"
            rules={[
              { required: true, message: 'Bitte URL eingeben' },
              {
                pattern: /^https?:\/\/.+/,
                message: 'Muss mit http:// oder https:// beginnen',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Beschreibung (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ApiPresets;
